import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { api, ApiError } from "../lib/api";
import type {
  AgreementClause,
  AgreementField,
  AgreementTemplateDTO,
} from "../../shared/types";
import { useGoBack } from "../../shared/phone/history";
import { usePhone, useNative } from "../../shared/phone/media";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { Chevron, SaveBar } from "../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../shared/phone/Sheet";
import { Icon } from "../components/ui/Icon";
import { SayToast } from "./partners/parts";
import "../styles/agreement-builder.css";

/**
 * Agreement builder (node B11) at /products/:id/agreement, opened from the
 * program settings Documents "Edit". Loads the product's real agreement template
 * (api.agreements.get), lets the owner edit the numbered clauses and place the
 * Signature and Date fields on the page, then saves it back (api.agreements.save).
 * "Save" keeps the version; "Publish new version" bumps it, which forces every
 * partner on an older version to re-sign (S3's re-sign logic keys on the version).
 *
 * MONEY RULE (load-bearing): the Payment clause must keep "Flashadz records amounts
 * and does not move money." Its clause cannot be removed here, and the server (S3)
 * rejects a save that drops the line; that rejection is surfaced honestly.
 */

/** The phrase every published agreement must keep (mirrors the S3 server guard). */
const MONEY_RULE_PHRASE = "does not move money";

/** Pixel widths that mirror the prototype's signature vs date field tags. */
const FIELD_WIDTH_PX: Record<AgreementField["type"], number> = {
  signature: 200,
  date: 130,
};

interface ClauseDraft {
  key: string;
  title: string;
  body: string;
}

interface FieldDraft {
  key: string;
  type: AgreementField["type"];
  page: number;
  x: number;
  y: number;
}

function newKey(): string {
  return Math.random().toString(36).slice(2, 9);
}

function hasMoneyRule(title: string, body: string): boolean {
  return `${title} ${body}`.toLowerCase().includes(MONEY_RULE_PHRASE);
}

function clausesToDrafts(clauses: AgreementClause[]): ClauseDraft[] {
  return clauses.map((c) => ({ key: newKey(), title: c.title, body: c.body }));
}

/** The clause key that currently holds the money rule, or null if none does. */
function moneyRuleKey(clauses: ClauseDraft[]): string | null {
  return clauses.find((c) => hasMoneyRule(c.title, c.body))?.key ?? null;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, hi));
}

function BackIcon() {
  return (
    <svg className="bk-ic" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

// ── The two-page paper preview (page 1 = clauses, page 2 = signatures) ────────

function PaperPage({
  pageNo,
  title,
  sub,
  registerRef,
  children,
}: {
  pageNo: number;
  title: string;
  sub: string;
  registerRef: (pageNo: number, el: HTMLDivElement | null) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="ab-page" ref={(el) => registerRef(pageNo, el)}>
      <div className="ab-dh">{title}</div>
      <div className="ab-dsub">{sub}</div>
      {children}
    </div>
  );
}

export function AgreementBuilder() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const native = useNative();
  const phone = usePhone();
  const goBack = useGoBack();
  // The phone's sheets: a clause open to edit (null key = a new one), and the confirms.
  const [clauseOpen, setClauseOpen] = useState<{ key: string | null } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [fieldMenu, setFieldMenu] = useState<string | null>(null);
  const [addingField, setAddingField] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const navState = useLocation().state as { productName?: string } | null;

  const [productName, setProductName] = useState<string>(navState?.productName ?? "");
  const [version, setVersion] = useState<number | null>(null);
  const [pdfR2Key, setPdfR2Key] = useState<string | null>(null);
  const [clauses, setClauses] = useState<ClauseDraft[]>([]);
  const [fields, setFields] = useState<FieldDraft[]>([]);
  const [protectedKey, setProtectedKey] = useState<string | null>(null);
  const [saved, setSaved] = useState<AgreementTemplateDTO | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);

  // toast (matches the partners/asset-library pattern)
  const [toast, setToast] = useState<{ msg: string; color: string; show: boolean }>({
    msg: "",
    color: "var(--violet)",
    show: false,
  });
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string, color = "var(--violet)") => {
    setToast({ msg, color, show: true });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 2800);
  }, []);
  useEffect(() => () => void (toastTimer.current && window.clearTimeout(toastTimer.current)), []);

  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const registerRef = useCallback((pageNo: number, el: HTMLDivElement | null) => {
    pageRefs.current[pageNo] = el;
  }, []);

  // ── load the real template ──
  const hydrate = useCallback((tpl: AgreementTemplateDTO) => {
    const drafts = clausesToDrafts(tpl.clauses);
    setClauses(drafts);
    setProtectedKey(moneyRuleKey(drafts));
    setFields(
      tpl.fields.map((f) => ({ key: newKey(), type: f.type, page: f.page, x: f.x, y: f.y })),
    );
    setVersion(tpl.version);
    setPdfR2Key(tpl.pdfR2Key);
    setSaved(tpl);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadErr(null);
    api.agreements
      .get(id)
      .then((res) => {
        if (!alive) return;
        hydrate(res.template);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setLoadErr(e instanceof ApiError ? e.message : String(e));
        setLoading(false);
      });
    // Best-effort product name for the paper title, when not carried in nav state.
    if (!navState?.productName) {
      api.products
        .detail(id)
        .then((d) => alive && setProductName(d.product.name))
        .catch(() => {});
    }
    return () => {
      alive = false;
    };
  }, [id, hydrate, navState?.productName]);

  // ── clause editing ──
  const setClauseTitle = (key: string, title: string) => {
    setClauses((prev) => prev.map((c) => (c.key === key ? { ...c, title } : c)));
  };
  const setClauseBody = (key: string, body: string) => {
    setClauses((prev) => prev.map((c) => (c.key === key ? { ...c, body } : c)));
  };
  const removeClause = (key: string) => {
    if (key === protectedKey) return;
    setClauses((prev) => prev.filter((c) => c.key !== key));
  };
  const addClause = () => {
    setClauses((prev) => [...prev, { key: newKey(), title: "New clause", body: "" }]);
  };

  // ── field placement ──
  const addField = (type: AgreementField["type"]) => {
    // Drop new fields onto the signatures page, staggered so repeats do not stack.
    setFields((prev) => {
      const onPage2 = prev.filter((f) => f.page === 2).length;
      return [
        ...prev,
        { key: newKey(), type, page: 2, x: 0.1, y: clamp(0.18 + onPage2 * 0.1, 0.18, 0.82) },
      ];
    });
  };
  const removeField = (key: string) => {
    setFields((prev) => prev.filter((f) => f.key !== key));
  };

  // Real drag: follow the pointer, snap to whichever page it is over, store the
  // field's top-left as a normalized (x, y) fraction of that page.
  const dragRef = useRef<{ key: string; grabX: number; grabY: number; w: number; h: number } | null>(
    null,
  );
  const onFieldPointerDown = (e: React.PointerEvent<HTMLDivElement>, field: FieldDraft) => {
    if ((e.target as HTMLElement).classList.contains("ab-fx")) return;
    e.preventDefault();
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      key: field.key,
      grabX: e.clientX - rect.left,
      grabY: e.clientY - rect.top,
      w: el.offsetWidth,
      h: el.offsetHeight,
    };
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragUp);
    // Also listen for mouse events: some drivers (and older browsers) emit mouse
    // moves without a paired pointermove, so a pointer-only listener can miss them.
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragUp);
  };
  const onDragMove = useCallback((e: PointerEvent | MouseEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    // Which page is the pointer over? Fall back to the field's current page.
    let targetPage = 0;
    let rect: DOMRect | null = null;
    for (const [pageNo, el] of Object.entries(pageRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
        targetPage = Number(pageNo);
        rect = r;
        break;
      }
    }
    setFields((prev) =>
      prev.map((f) => {
        if (f.key !== drag.key) return f;
        const page = targetPage || f.page;
        const box = rect ?? pageRefs.current[page]?.getBoundingClientRect() ?? null;
        if (!box) return f;
        const wFrac = drag.w / box.width;
        const hFrac = drag.h / box.height;
        const x = clamp((e.clientX - drag.grabX - box.left) / box.width, 0, Math.max(0, 1 - wFrac));
        const y = clamp((e.clientY - drag.grabY - box.top) / box.height, 0, Math.max(0, 1 - hFrac));
        return { ...f, page, x, y };
      }),
    );
  }, []);
  const onDragUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragUp);
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragUp);
  }, [onDragMove]);
  useEffect(
    () => () => {
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragUp);
      window.removeEventListener("mousemove", onDragMove);
      window.removeEventListener("mouseup", onDragUp);
    },
    [onDragMove, onDragUp],
  );

  // ── save ──
  const buildPayload = useCallback(() => {
    const outClauses: AgreementClause[] = clauses.map((c, i) => ({
      n: i + 1,
      title: c.title.trim(),
      body: c.body.trim(),
    }));
    const outFields: AgreementField[] = fields.map((f) => ({
      type: f.type,
      page: f.page,
      x: Number(f.x.toFixed(4)),
      y: Number(f.y.toFixed(4)),
    }));
    return { clauses: outClauses, fields: outFields, pdfR2Key };
  }, [clauses, fields, pdfR2Key]);

  const doSave = useCallback(
    async (bumpVersion: boolean) => {
      if (saving || loading) return;
      setSaving(true);
      setSaveErr(null);
      try {
        const res = await api.agreements.save(id, { ...buildPayload(), bumpVersion });
        hydrate(res.template);
        if (bumpVersion) {
          showToast(`Published version ${res.template.version}. Partners will re-sign.`, "var(--win)");
        } else {
          showToast("Agreement saved", "var(--win)");
        }
      } catch (e) {
        setSaveErr(e instanceof ApiError ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [saving, loading, id, buildPayload, hydrate, showToast],
  );

  const moneyRuleOk = moneyRuleKey(clauses) !== null;

  if (native) {
    const documents = `/products/${encodeURIComponent(id)}/partners?program=documents`;
    const dirty = !!saved && draftShape(clauses, fields) !== draftShape(clausesToDrafts(saved.clauses), saved.fields);
    const editing = clauseOpen ? (clauseOpen.key ? clauses.find((c) => c.key === clauseOpen.key) ?? null : null) : null;
    const removingClause = removing ? clauses.find((c) => c.key === removing) ?? null : null;
    const menuField = fieldMenu ? fields.find((f) => f.key === fieldMenu) ?? null : null;
    const pages = [1, 2].filter((n) => n === 2 || fields.some((f) => f.page === n));
    const addNativeField = (type: AgreementField["type"]) => {
      const slot = freeSlot(fields, type);
      if (!slot) {
        showToast("The page is full. Remove a field first.", "var(--warn)");
        return;
      }
      setFields((prev) => [...prev, { key: newKey(), type, page: 2, ...slot }]);
    };
    return (
      <div className="pn-screen pa-agscreen">
        <PhoneNav
          title={
            // Why: the bar has room for "Agreement" beside Documents; the large title says it in full.
            <>
              <span className="pa-t-long">Partner agreement</span>
              <span className="pa-t-short">Agreement</span>
            </>
          }
          large
          back={{ to: documents, label: "Documents", onBack: () => (dirty ? setLeaving(true) : goBack(documents)) }}
          sub={version !== null ? `Version ${version}` : undefined}
          actions={
            <button type="button" className="pn-tbtn" aria-label="More for the agreement" onClick={() => setMoreOpen(true)} disabled={loading || !!loadErr}>
              <Icon name="more" />
            </button>
          }
        />
        {loading ? (
          <div className="pn-empty">Loading the agreement…</div>
        ) : loadErr ? (
          <div className="pn-empty">
            <b>Couldn't load the agreement</b>
            {loadErr}
          </div>
        ) : (
          <div className="pa-ag">
            {saveErr || !moneyRuleOk ? (
              <div className="pa-ag-notes">
                {saveErr ? (
                  <p className="pa-err" role="alert">
                    {saveErr}
                  </p>
                ) : null}
                {!moneyRuleOk ? (
                  <p className="pa-err warn" role="status">
                    The Payment clause must keep the line “Flashadz records amounts and does not move money.” Saving without it is refused.
                  </p>
                ) : null}
              </div>
            ) : null}
            <section className="pa-agc">
              <div className="pn-sh">
                <h2>Clauses</h2>
              </div>
              <div className="pn-grp">
                {clauses.map((c, i) => {
                  const locked = c.key === protectedKey;
                  return (
                    <button key={c.key} type="button" className="pn-row pa-cl" onClick={() => setClauseOpen({ key: c.key })}>
                      <span className="pa-num">{i + 1}</span>
                      <span className="m">
                        <span className="t">
                          {c.title || "Untitled"}
                          {locked ? <span className="pn-pill warn">Money rule</span> : null}
                        </span>
                        <span className="s">{c.body || "Nothing written yet."}</span>
                      </span>
                      {locked ? (
                        <span className="pa-lk" aria-label="Kept: it holds the money rule">
                          <Icon name="lock" />
                        </span>
                      ) : (
                        <Chevron />
                      )}
                    </button>
                  );
                })}
                <button type="button" className="pn-row accent" onClick={() => setClauseOpen({ key: null })}>
                  <span className="pn-ico">
                    <Icon name="plus" />
                  </span>
                  <span className="m">
                    <span className="t">Add clause</span>
                  </span>
                </button>
              </div>
            </section>
            <section className="pa-agp">
              <div className="pn-sh">
                <h2>Where partners sign</h2>
              </div>
              <div className="pn-pad pa-papers">
                {pages.map((n) => (
                  <div className="pa-paper" key={n}>
                    <div className="pa-pg">
                      <b className="pa-pgt">{n === 2 ? "Signatures" : `${productName || "Partner"} Agreement`}</b>
                      <span className="pa-pgs">Page {n} of 2</span>
                      {n === 2 ? <p className="pa-pgp">By signing below, the Partner agrees to the terms of this agreement.</p> : null}
                      {fields
                        .filter((f) => f.page === n)
                        .map((f) => {
                          const label = f.type === "signature" ? "Signature" : "Date";
                          return (
                            <button
                              key={f.key}
                              type="button"
                              className={`pa-fld ${f.type}`}
                              style={{
                                left: `${f.x * 100}%`,
                                top: `${f.y * 100}%`,
                                width: `${fieldWidth(f, fields) * 100}%`,
                                height: `${FIELD_H * 100}%`,
                              }}
                              onClick={() => setFieldMenu(f.key)}
                              aria-label={`${label} field`}
                            >
                              <i>{label}</i>
                              <span>{f.type === "signature" ? "Partner signs here" : "Date signed"}</span>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="pn-grp pa-gap">
                <button type="button" className="pn-row accent" onClick={() => setAddingField(true)}>
                  <span className="pn-ico">
                    <Icon name="plus" />
                  </span>
                  <span className="m">
                    <span className="t">Add a field</span>
                  </span>
                </button>
              </div>
              <p className="pn-note">Tap a field on the page to remove it.</p>
            </section>
          </div>
        )}
        <SaveBar dirty={dirty} saving={saving} onSave={() => void doSave(false)} onDiscard={() => saved && hydrate(saved)} />

        {clauseOpen ? (
          <ClauseSheet
            clause={editing}
            index={editing ? clauses.indexOf(editing) : clauses.length}
            locked={!!editing && editing.key === protectedKey}
            tall={phone}
            onClose={() => setClauseOpen(null)}
            onDone={(title, body) => {
              if (editing) {
                setClauseTitle(editing.key, title);
                setClauseBody(editing.key, body);
              } else {
                setClauses((prev) => [...prev, { key: newKey(), title, body }]);
              }
              setClauseOpen(null);
            }}
            onRemove={() => {
              if (!editing) return;
              setClauseOpen(null);
              setRemoving(editing.key);
            }}
          />
        ) : null}
        <ActionSheet
          open={!!removingClause}
          onClose={() => setRemoving(null)}
          title={removingClause ? `Remove “${removingClause.title || "this clause"}”?` : undefined}
          message="It comes out of the agreement when you save."
          actions={[
            {
              label: "Remove clause",
              danger: true,
              onSelect: () => {
                if (!removingClause) return;
                removeClause(removingClause.key);
                showToast(`${removingClause.title || "The clause"} removed. Discard puts it back.`);
              },
            },
          ]}
        />
        <ActionSheet
          open={!!menuField}
          onClose={() => setFieldMenu(null)}
          title={menuField?.type === "date" ? "Date field" : "Signature field"}
          message={menuField?.type === "date" ? "Filled in when they sign." : "Partners sign here."}
          actions={[{ label: "Remove", danger: true, onSelect: () => menuField && removeField(menuField.key) }]}
        />
        <ActionSheet
          open={addingField}
          onClose={() => setAddingField(false)}
          title="Add a field"
          actions={[
            { label: "Signature", onSelect: () => addNativeField("signature") },
            { label: "Date", onSelect: () => addNativeField("date") },
          ]}
        />
        <ActionSheet
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          title="Partner agreement"
          message={version !== null ? `Version ${version}` : undefined}
          actions={[{ label: "Publish new version", onSelect: () => setPublishOpen(true) }]}
        />
        <ActionSheet
          open={publishOpen}
          onClose={() => setPublishOpen(false)}
          title="Publish a new version?"
          message={`Publishing makes version ${version !== null ? version + 1 : "the next version"}. Every partner who signed an older version must sign again before their next payout. Use Save to fix wording without that.`}
          actions={[{ label: "Publish and ask partners to sign again", onSelect: () => void doSave(true) }]}
        />
        <ActionSheet
          open={leaving}
          onClose={() => setLeaving(false)}
          title="Leave without saving?"
          message="Your changes to the agreement are not saved."
          actions={[{ label: "Discard changes", danger: true, onSelect: () => goBack(documents) }]}
        />
        <SayToast toast={{ msg: toast.msg, on: toast.show, done: toast.color !== "var(--warn)" }} />
      </div>
    );
  }

  return (
    <section className="agreement view-wrap">
      <button
        type="button"
        className="back"
        onClick={() => navigate(`/products/${encodeURIComponent(id)}/partners`)}
      >
        <BackIcon />
        {productName || "Partners"}
      </button>

      <div className="ab-head">
        <div>
          <div className="eyebrow">Agreement</div>
          <h1>Partner agreement</h1>
          <p className="lede">
            Upload your PDF, then drag the fields onto the page where partners sign.
          </p>
        </div>
        <div className="ab-actions">
          {/* U0: no real PDF-upload route exists yet (S3 exposes GET/PUT
              /products/:id/agreement only). Upload is deferred, not faked; the
              agreement is built from the clauses below. See GRAPH-v2 section 9. */}
          <button
            type="button"
            className="btn ghost"
            disabled
            title="PDF upload arrives soon. For now the agreement is built from the clauses below."
          >
            Upload PDF
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => setConfirmPublish(true)}
            disabled={saving || loading || !!loadErr}
          >
            Publish new version
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => doSave(false)}
            disabled={saving || loading || !!loadErr}
          >
            {saving ? "Saving" : "Save"}
          </button>
        </div>
      </div>

      {loading && <div className="ab-load">Loading the agreement</div>}
      {!loading && loadErr && (
        <div className="ab-load err">Couldn't load the agreement: {loadErr}</div>
      )}

      {!loading && !loadErr && (
        <>
          <div className="ab-toolbar">
            <span className="ab-file">
              {pdfR2Key ? pdfR2Key.split("/").pop() : "Built from the clauses"}
            </span>
            <span className="ab-note">
              {version !== null ? `Version ${version}` : ""} · 2 pages
            </span>
            <span className="ab-addlbl">Add field:</span>
            <button type="button" className="btn ghost sm" onClick={() => addField("signature")}>
              + Signature
            </button>
            <button type="button" className="btn ghost sm" onClick={() => addField("date")}>
              + Date
            </button>
            <span className="ab-note push">Drag a field to any page and drop it where it belongs.</span>
          </div>

          {saveErr && (
            <div className="ab-banner err" role="alert">
              {saveErr}
            </div>
          )}
          {!moneyRuleOk && (
            <div className="ab-banner warn" role="status">
              The Payment clause must keep the line "Flashadz records amounts and does not move
              money." Saving without it will be rejected.
            </div>
          )}

          <div className="ab-docwrap">
            {/* Page 1: the editable clauses */}
            <PaperPage
              pageNo={1}
              title={`${productName || "Partner"} Agreement`}
              sub={`Between ${productName || "the company"} and the Partner · Page 1 of 2`}
              registerRef={registerRef}
            >
              <div className="ab-clauses">
                {clauses.map((c, i) => {
                  const locked = c.key === protectedKey;
                  return (
                    <div className={`ab-clause${locked ? " locked" : ""}`} key={c.key}>
                      <div className="ab-crow">
                        <span className="ab-cnum">{i + 1}.</span>
                        <input
                          className="ab-ctitle"
                          value={c.title}
                          onChange={(e) => setClauseTitle(c.key, e.target.value)}
                          aria-label={`Clause ${i + 1} title`}
                          placeholder="Clause title"
                        />
                        {locked ? (
                          <span className="ab-lock" title="Keeps the money rule and can't be removed">
                            Money rule
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="ab-cdel"
                            onClick={() => removeClause(c.key)}
                            aria-label={`Remove clause ${i + 1}`}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <textarea
                        className="ab-cbody"
                        value={c.body}
                        onChange={(e) => setClauseBody(c.key, e.target.value)}
                        aria-label={`Clause ${i + 1} body`}
                        placeholder="What this clause says"
                        rows={2}
                      />
                    </div>
                  );
                })}
              </div>
              <button type="button" className="ab-addclause" onClick={addClause}>
                + Add clause
              </button>

              {fields
                .filter((f) => f.page === 1)
                .map((f) => (
                  <FieldTag key={f.key} field={f} onPointerDown={onFieldPointerDown} onRemove={removeField} />
                ))}
            </PaperPage>

            {/* Page 2: the signatures page + placed fields */}
            <PaperPage
              pageNo={2}
              title="Signatures"
              sub="Page 2 of 2"
              registerRef={registerRef}
            >
              <p className="ab-signline">
                By signing below, the Partner agrees to the terms of this agreement.
              </p>
              {fields
                .filter((f) => f.page === 2)
                .map((f) => (
                  <FieldTag key={f.key} field={f} onPointerDown={onFieldPointerDown} onRemove={removeField} />
                ))}
            </PaperPage>
          </div>
        </>
      )}

      {confirmPublish && (
        <div
          className="ab-scrim"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirmPublish(false);
          }}
        >
          <div className="ab-modal" role="dialog" aria-modal="true" aria-label="Publish a new version">
            <div className="ab-mhead">
              <h3>Publish a new version?</h3>
            </div>
            <div className="ab-mbody">
              <p>
                Publishing makes version {version !== null ? version + 1 : "the next version"}. Every
                partner who signed an older version must sign again before their next payout.
              </p>
              <p className="ab-msub">Use Save instead to fix wording without a re-sign.</p>
            </div>
            <div className="ab-mfoot">
              <button
                type="button"
                className="btn ghost"
                onClick={() => setConfirmPublish(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={async () => {
                  setConfirmPublish(false);
                  await doSave(true);
                }}
                disabled={saving}
              >
                Publish and force re-sign
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`ab-toast ${toast.show ? "show" : ""}`} role="status" aria-live="polite">
        <span className="tdot" style={{ background: toast.color }} />
        <span className="msg">{toast.msg}</span>
      </div>
    </section>
  );
}

// ── one draggable Signature / Date tag on the paper ───────────────────────────

function FieldTag({
  field,
  onPointerDown,
  onRemove,
}: {
  field: FieldDraft;
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>, field: FieldDraft) => void;
  onRemove: (key: string) => void;
}) {
  return (
    <div
      className="ab-field"
      style={{
        left: `${field.x * 100}%`,
        top: `${field.y * 100}%`,
        width: `${FIELD_WIDTH_PX[field.type]}px`,
      }}
      onPointerDown={(e) => onPointerDown(e, field)}
    >
      <span className="ab-sigtag">{field.type === "signature" ? "Signature" : "Date"}</span>
      {field.type === "signature" ? "Partner signs here" : "Date"}
      <button
        type="button"
        className="ab-fx"
        tabIndex={-1}
        onClick={() => onRemove(field.key)}
        aria-label="Remove field"
      >
        ×
      </button>
    </div>
  );
}

// ── the phone's paper: fields sized as parts of the page, so they never overlap ──

/** A field's height and its widest, as fractions of the page. */
const FIELD_H = 0.11;
const FIELD_W: Record<AgreementField["type"], number> = { signature: 0.46, date: 0.33 };
/** Where a new field goes on the signatures page, first free spot first. */
const SLOTS = [
  { x: 0.08, y: 0.6 },
  { x: 0.62, y: 0.6 },
  { x: 0.08, y: 0.78 },
  { x: 0.62, y: 0.78 },
];
const GAP = 0.02;

/** A field's width on the phone's paper: its own, cut short of the page edge and of the next field on its line. */
function fieldWidth(f: FieldDraft, all: FieldDraft[]): number {
  let room = 0.97 - f.x;
  for (const o of all) {
    if (o.key === f.key || o.page !== f.page || Math.abs(o.y - f.y) >= FIELD_H || o.x <= f.x) continue;
    room = Math.min(room, o.x - f.x - GAP);
  }
  return Math.max(0.12, Math.min(FIELD_W[f.type], room));
}

/** The first spot on the signatures page where a new field of this kind overlaps nothing. */
function freeSlot(all: FieldDraft[], type: AgreementField["type"]): { x: number; y: number } | null {
  const w = FIELD_W[type];
  const onPage = all.filter((f) => f.page === 2);
  return (
    SLOTS.find(
      (s) =>
        s.x + w <= 0.97 &&
        !onPage.some((f) => s.x < f.x + fieldWidth(f, all) && f.x < s.x + w && s.y < f.y + FIELD_H && f.y < s.y + FIELD_H),
    ) ?? null
  );
}

/** The parts of a draft a save would change, to tell whether anything did. */
function draftShape(clauses: ClauseDraft[], fields: Array<Pick<AgreementField, "type" | "page" | "x" | "y">>): string {
  return JSON.stringify({
    c: clauses.map((c) => [c.title.trim(), c.body.trim()]),
    f: fields.map((f) => [f.type, f.page, Number(f.x.toFixed(4)), Number(f.y.toFixed(4))]),
  });
}

/** A clause opened on a phone: its title and words at 16px, Remove clause in red (it asks first). */
function ClauseSheet({
  clause,
  index,
  locked,
  tall,
  onClose,
  onDone,
  onRemove,
}: {
  clause: ClauseDraft | null;
  index: number;
  locked: boolean;
  tall: boolean;
  onClose: () => void;
  onDone: (title: string, body: string) => void;
  onRemove: () => void;
}) {
  const [title, setTitle] = useState(clause?.title ?? "");
  const [body, setBody] = useState(clause?.body ?? "");
  const [err, setErr] = useState<string | null>(null);
  const done = () => {
    const t = title.trim();
    const b = body.trim();
    if (!t || !b) {
      setErr("Give the clause a title and its words.");
      return;
    }
    if (locked && !hasMoneyRule(t, b)) {
      setErr(`Keep “${MONEY_RULE_PHRASE}” in this clause.`);
      return;
    }
    onDone(t, b);
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title={clause ? `Clause ${index + 1}` : "New clause"}
      tall={tall}
      className="pa-form-sheet"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      right={
        <button type="button" className="pn-tbtn strong" onClick={done}>
          Done
        </button>
      }
    >
      <div className="pn-grp">
        <div className="pn-irow col">
          <label htmlFor="pa-cl-title">Title</label>
          <input id="pa-cl-title" value={title} placeholder="Refunds" autoComplete="off" onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="pn-irow col">
          <label htmlFor="pa-cl-body">What it says</label>
          <textarea id="pa-cl-body" rows={7} value={body} placeholder="Write the clause in plain words." onChange={(e) => setBody(e.target.value)} />
        </div>
      </div>
      {err ? (
        <p className="pa-err" role="alert">
          {err}
        </p>
      ) : null}
      {locked ? (
        <p className="pn-note pa-lockline">
          <Icon name="lock" />
          <span>This clause keeps the money rule, so it stays. Its words must still say Flashadz “{MONEY_RULE_PHRASE}”.</span>
        </p>
      ) : clause ? (
        <div className="pn-grp pa-gap">
          <button type="button" className="pn-row danger" onClick={onRemove}>
            <span className="m">
              <span className="t">Remove clause</span>
            </span>
          </button>
        </div>
      ) : null}
    </Sheet>
  );
}
